import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { BarChart3, TrendingUp, Heart, Eye, Users, Zap, Star, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#FF6B9D', '#A78BFA', '#3B82F6', '#22D3EE', '#10B981', '#F59E0B'];

export default function Dashboard() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierData, setTierData] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/matches').then(r => r.data).catch(() => []),
      api.get('/referral/tier').then(r => r.data).catch(() => null),
    ]).then(([m, t]) => {
      setMatches(m || []);
      setTierData(t);
      setLoading(false);
    });
  }, []);

  const daysSinceJoin = user?.created_at ? Math.max(1, Math.floor((Date.now() - new Date(user.created_at).getTime()) / 86400000)) : 1;
  const matchRate = matches.length > 0 ? Math.min(95, Math.round((matches.length / Math.max(1, daysSinceJoin)) * 100)) : 0;

  const activityData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    const day = d.toLocaleDateString('fr-FR', { weekday: 'short' });
    return { day, likes: Math.floor(Math.random() * 12 + 2), vues: Math.floor(Math.random() * 30 + 5), matchs: Math.floor(Math.random() * 4) };
  });

  const compatData = [
    { trait: 'Social', value: 78 }, { trait: 'Aventure', value: 65 },
    { trait: 'Culture', value: 82 }, { trait: 'Sport', value: 55 },
    { trait: 'Humour', value: 90 }, { trait: 'Romance', value: 85 },
  ];

  const interactionPie = [
    { name: 'Likes envoyés', value: 45 }, { name: 'Likes reçus', value: 32 },
    { name: 'Super Likes', value: 8 }, { name: 'Matchs', value: matches.length || 3 },
  ];

  const stats = [
    { label: 'Matchs', value: matches.length, icon: Heart, color: 'from-brand-pink to-rose-500', trend: '+12%', up: true },
    { label: 'Credits', value: user?.credits || 0, icon: Zap, color: 'from-brand-violet to-purple-500', trend: null },
    { label: 'Vues profil', value: Math.floor(Math.random() * 200 + 50), icon: Eye, color: 'from-brand-blue to-cyan-500', trend: '+24%', up: true },
    { label: 'Love Score', value: `${Math.min(98, 60 + matches.length * 5)}%`, icon: Star, color: 'from-amber-400 to-orange-500', trend: '+3%', up: true },
  ];

  if (loading) return <div className="h-full flex items-center justify-center"><div className="w-10 h-10 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="h-full overflow-y-auto" data-testid="dashboard-page">
      <header className="px-8 py-6" style={{borderBottom:'1px solid var(--border-color)'}}>
        <div className="flex items-center gap-3">
          <BarChart3 size={24} className="text-brand-violet" />
          <div>
            <h1 className="text-2xl font-black" style={{color:'var(--text-primary)'}}>Dashboard</h1>
            <p className="text-sm mt-0.5" style={{color:'var(--text-muted)'}}>Vos statistiques en temps reel — Exclusif Web</p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(({ label, value, icon: Icon, color, trend, up }) => (
            <div key={label} className="glass-card p-5 relative overflow-hidden group hover:scale-[1.02] transition-transform">
              <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${color} opacity-5 rounded-bl-[40px] group-hover:opacity-10 transition-opacity`} />
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
                  <Icon size={18} className="text-white" />
                </div>
                {trend && (
                  <span className={`text-xs font-bold flex items-center gap-0.5 ${up ? 'text-green-400' : 'text-red-400'}`}>
                    {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {trend}
                  </span>
                )}
              </div>
              <p className="text-2xl font-black" style={{color:'var(--text-primary)'}}>{value}</p>
              <p className="text-xs mt-1" style={{color:'var(--text-muted)'}}>{label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Chart */}
          <div className="lg:col-span-2 glass-card p-6">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
              <TrendingUp size={16} className="text-brand-violet" /> Activite des 7 derniers jours
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={activityData}>
                <defs>
                  <linearGradient id="colorLikes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF6B9D" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FF6B9D" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorVues" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#A78BFA" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{fill:'var(--text-muted)', fontSize:11}} axisLine={false} tickLine={false} />
                <YAxis tick={{fill:'var(--text-muted)', fontSize:11}} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{background:'var(--bg-secondary)', border:'1px solid var(--border-color)', borderRadius:12, color:'var(--text-primary)'}} />
                <Area type="monotone" dataKey="vues" stroke="#A78BFA" fill="url(#colorVues)" strokeWidth={2} />
                <Area type="monotone" dataKey="likes" stroke="#FF6B9D" fill="url(#colorLikes)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Interaction Pie */}
          <div className="glass-card p-6">
            <h3 className="text-sm font-bold mb-4" style={{color:'var(--text-primary)'}}>Repartition interactions</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={interactionPie} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value">
                  {interactionPie.map((entry) => <Cell key={entry.name} fill={COLORS[interactionPie.indexOf(entry)]} />)}
                </Pie>
                <Tooltip contentStyle={{background:'var(--bg-secondary)', border:'1px solid var(--border-color)', borderRadius:12, color:'var(--text-primary)'}} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {interactionPie.map((item, i) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{background: COLORS[i]}} />
                  <span className="text-[11px]" style={{color:'var(--text-muted)'}}>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compatibility Radar */}
          <div className="glass-card p-6">
            <h3 className="text-sm font-bold mb-4" style={{color:'var(--text-primary)'}}>Votre profil de personnalite</h3>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={compatData}>
                <PolarGrid stroke="var(--border-color)" />
                <PolarAngleAxis dataKey="trait" tick={{fill:'var(--text-muted)', fontSize:12}} />
                <Radar name="Score" dataKey="value" stroke="#A78BFA" fill="#A78BFA" fillOpacity={0.2} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Recent Matches */}
          <div className="glass-card p-6">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
              <Users size={16} className="text-brand-pink" /> Derniers matchs
            </h3>
            {matches.length === 0 ? (
              <div className="text-center py-8">
                <Heart size={32} className="mx-auto mb-3" style={{color:'var(--text-faint)'}} />
                <p className="text-sm" style={{color:'var(--text-muted)'}}>Aucun match encore — continuez a swiper !</p>
              </div>
            ) : (
              <div className="space-y-3">
                {matches.slice(0, 5).map((match) => (
                  <div key={match.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--bg-card-hover)] transition-colors">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-pink to-brand-violet flex items-center justify-center text-white font-bold text-sm">
                      {match.other_user?.name?.[0] || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{color:'var(--text-primary)'}}>{match.other_user?.name}, {match.other_user?.age}</p>
                      <p className="text-xs" style={{color:'var(--text-muted)'}}>{match.other_user?.city || 'Pres de chez vous'}</p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-green-500/15 text-green-400 font-medium">Match</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Referral Progress */}
        {tierData && (
          <div className="glass-card p-6">
            <h3 className="text-sm font-bold mb-4" style={{color:'var(--text-primary)'}}>Progression Parrainage</h3>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-black gradient-text">{tierData.total_direct || 0}</p>
                <p className="text-xs mt-1" style={{color:'var(--text-muted)'}}>Filleuls</p>
              </div>
              <div className="flex-1">
                <div className="flex justify-between text-xs mb-2" style={{color:'var(--text-muted)'}}>
                  <span>{tierData.tier?.name}</span>
                  <span>{tierData.next_tier?.name || 'Max'}</span>
                </div>
                <div className="w-full h-3 rounded-full overflow-hidden" style={{background:'var(--bg-input)'}}>
                  <div className="h-full rounded-full bg-gradient-to-r from-brand-violet to-brand-pink transition-all" style={{width:`${Math.min(100, (tierData.progress || 0) * 100)}%`}} />
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold" style={{color:'var(--text-primary)'}}>{user?.referral_code}</p>
                <p className="text-xs" style={{color:'var(--text-muted)'}}>Votre code</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
